import { useState, useEffect, useCallback, useRef } from 'react';
import { videoApi } from '../services/api';
import logger from '../lib/logger';

interface PollingTarget {
  id: string;
  type: 'video_status' | 'dashboard' | 'user_stats';
  priority: 'high' | 'normal' | 'low';
  callback: (data: any) => void;
  lastUpdate?: number;
  lastStatus?: any; // Store last successful status for rate limiting fallback
  errorCount?: number;
}

interface UseUnifiedPollingReturn {
  addTarget: (target: PollingTarget) => void;
  removeTarget: (id: string) => void;
  updateTargetPriority: (id: string, priority: 'high' | 'normal' | 'low') => void;
  isPolling: boolean;
  totalTargets: number;
}

export const useUnifiedPolling = (): UseUnifiedPollingReturn => {
  const [targets, setTargets] = useState<Map<string, PollingTarget>>(new Map());
  const [isPolling, setIsPolling] = useState(false);
  
  const _intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Intelligent polling intervals based on priority and error count
  const _getPollingInterval = useCallback((priority: 'high' | 'normal' | 'low', errorCount: number = 0) => {
    const baseIntervals = {
      high: 5000,   // 5 seconds for processing videos for snappier UX
      normal: 120000, // 2 minutes for normal updates (increased from 90s)
      low: 900000    // 15 minutes for low priority (increased from 10m)
    };
    
    // Exponential backoff on errors, but cap at reasonable limits
    const backoffMultiplier = Math.min(Math.pow(2, errorCount), 8); // Reduced max multiplier
    return Math.min(baseIntervals[priority] * backoffMultiplier, 600000); // Max 10 minutes
  }, []);

  // Batch API calls to reduce requests
  const performBatchedPolling = useCallback(async () => {
    if (targets.size === 0) return;

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      // Group targets by type for batching
      const videoStatusTargets = Array.from(targets.values()).filter(t => t.type === 'video_status');
      const dashboardTargets = Array.from(targets.values()).filter(t => t.type === 'dashboard');
      const statsTargets = Array.from(targets.values()).filter(t => t.type === 'user_stats');

      // Batch video status requests with better rate limiting handling
      if (videoStatusTargets.length > 0) {
        // Extract actual video IDs from prefixed target IDs
        const _videoIds = videoStatusTargets.map(t => t.id.replace('video_status_', ''));
        
        // Always make requests sequentially with delays to avoid rate limiting
        for (let i = 0; i < videoStatusTargets.length; i++) {
          if (signal.aborted) break;
          
          const target = videoStatusTargets[i];
          const actualVideoId = target.id.replace('video_status_', '');
          try {
            // Add delay between requests to avoid overwhelming the server
            // Shorter delay for processing videos to show progress updates faster
            if (i > 0) await new Promise(resolve => setTimeout(resolve, 2000)); // Reduced to 2 seconds for faster progress updates
            
            const status = await videoApi.getVideoStatus(actualVideoId);
            // console.log(`🔄 [Polling] Video ${actualVideoId} status:`, status);
            
            // Store the last successful status for rate limiting fallback
            target.lastStatus = status;
            
                          // **CRITICAL**: Auto-remove polling targets when video analysis is complete
              if (status.status === 'analyzed' || status.status === 'error') {
                logger.info(`🎯 Video ${actualVideoId} is complete (${status.status}), scheduling polling removal`);
                
                // Call the callback one final time with the completion status
                target.callback(status);
                
                // Remove this target from polling after a longer delay to ensure UI updates properly
                setTimeout(() => {
                  setTargets(prev => {
                    const newTargets = new Map(prev);
                    newTargets.delete(target.id);
                    // logger.info(`🗑️ Removed polling target for completed video ${actualVideoId}`);
                    return newTargets;
                  });
                }, 5000); // Increased delay to 5 seconds to ensure UI updates properly
                
                continue; // Skip normal callback processing since we already called it
              }
              
              // Enhanced completion detection - check for clips and high progress
              const hasClips = status.clips_count > 0;
              const errorMessage = status.error_message || '';
              const progressMatch = errorMessage.match(/(\d+(?:\.\d+)?)%/);
              const progress = progressMatch ? parseFloat(progressMatch[1]) : 0;
              const lowerMessage = errorMessage.toLowerCase();
              
              // More strict completion detection - only mark as complete if:
              // 1. Has clips AND progress is 100% OR
              // 2. Has clips AND message explicitly says "analysis complete" or "processing complete"
              const messageIndicatesComplete = lowerMessage.includes('analysis complete') ||
                                             lowerMessage.includes('processing complete') ||
                                             lowerMessage.includes('finished');
              
              const isActuallyComplete = hasClips && (progress >= 100 || (progress >= 90 && messageIndicatesComplete));
              
              if (isActuallyComplete) {
                // console.log(`🎉 [Polling] Video ${actualVideoId} appears complete - clips: ${status.clips_count}, progress: ${progress}%, message: ${errorMessage}`);
                
                // Modify status to indicate completion for the callback
                const completionStatus = {
                  ...status,
                  status: 'analyzed', // Override to trigger completion logic
                  progress: 100
                };
                
                // Call the callback one final time with the completion status
                target.callback(completionStatus);
                
                // Remove this target from polling after delay
                setTimeout(() => {
                  setTargets(prev => {
                    const newTargets = new Map(prev);
                    newTargets.delete(target.id);
                    // logger.info(`🗑️ Removed polling target for video with clips ${actualVideoId}`);
                    return newTargets;
                  });
                }, 5000); // 5 second delay
                
                continue; // Skip normal callback processing since we already called it
              }
            
            // console.log(`📞 [Polling] Calling callback for video ${actualVideoId}:`, status);
            target.callback(status);
            target.lastUpdate = Date.now();
            target.errorCount = 0;
          } catch (error: any) {
            if (!signal.aborted) {
              target.errorCount = (target.errorCount || 0) + 1;
              
              // Handle 429 rate limiting gracefully
              if (error.response?.status === 429 || error.message?.includes('429') || error.message?.includes('Too many')) {
                logger.warn(`Rate limited for video ${actualVideoId}, backing off polling (error count: ${target.errorCount})`);
                
                // Much less aggressive backoff for rate limiting - keep UI responsive
                const backoffDelay = Math.min(1000 + (target.errorCount * 500), 5000); // 1.5s to 5s max
                
                // Still call the callback with last known status to keep UI alive
                // This prevents the UI from disappearing during rate limiting
                const lastStatus = target.lastStatus || { 
                  status: 'processing', 
                  error_message: 'Checking progress...', 
                  clips_count: 0 
                };
                target.callback(lastStatus);
                
                setTimeout(() => {
                  // This will cause the next polling cycle to be delayed
                }, backoffDelay);
              } else {
                logger.warn(`Video status polling error for ${actualVideoId}:`, error);
              }
            }
          }
        }
      }

      // Dashboard updates (less frequent)
      if (dashboardTargets.length > 0) {
        const now = Date.now();
        const dashboardTarget = dashboardTargets[0]; // Only one dashboard typically
        
        // Only update dashboard if it hasn't been updated recently (increased from 180s to 300s)
        if (!dashboardTarget.lastUpdate || now - dashboardTarget.lastUpdate > 300000) {
          try {
            const [videos, stats] = await Promise.all([
              videoApi.getVideos(),
              videoApi.getStats()
            ]);
            
            dashboardTarget.callback({ videos, stats });
            dashboardTarget.lastUpdate = now;
            dashboardTarget.errorCount = 0;
          } catch (error: any) {
            if (!signal.aborted) {
              dashboardTarget.errorCount = (dashboardTarget.errorCount || 0) + 1;
              logger.warn('Dashboard polling error:', error);
            }
          }
        }
      }

      // User stats (least frequent)
      if (statsTargets.length > 0) {
        const now = Date.now();
        const statsTarget = statsTargets[0];
        
        // Only update stats if they haven't been updated recently (increased from 600s to 900s)
        if (!statsTarget.lastUpdate || now - statsTarget.lastUpdate > 900000) {
          try {
            const stats = await videoApi.getStats();
            statsTarget.callback(stats);
            statsTarget.lastUpdate = now;
            statsTarget.errorCount = 0;
          } catch (error: any) {
            if (!signal.aborted) {
              statsTarget.errorCount = (statsTarget.errorCount || 0) + 1;
              logger.warn('Stats polling error:', error);
            }
          }
        }
      }

      // Reset consecutive errors on successful batch
      consecutiveErrorsRef.current = 0;

    } catch (error: any) {
      if (!signal.aborted) {
        consecutiveErrorsRef.current++;
        logger.error(`Batch polling error (${consecutiveErrorsRef.current} consecutive):`, error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets]);

  // Start polling with configurable interval
  const startPolling = useCallback((intervalMs: number = 3000) => { // 3s default for more responsive status
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const poll = () => {
      if (targets.size > 0) {
        performBatchedPolling().catch(error => {
          logger.error('Polling error:', error);
          
          // If we get rate limited, moderately increase the polling interval
          if (error.response?.status === 429 || error.message?.includes('429')) {
            const newInterval = Math.min(intervalMs * 1.5, 10000); // 1.5x interval, max 10 seconds
            logger.warn(`Rate limited, increasing polling interval to ${newInterval}ms`);
            startPolling(newInterval);
            return;
          }
        });
      }
    };

    poll(); // Immediate first poll
    pollingIntervalRef.current = setInterval(poll, intervalMs);
    setIsPolling(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performBatchedPolling]);

  // Add a new polling target
  const addTarget = useCallback((target: PollingTarget) => {
    setTargets(prev => {
      // Prevent duplicate targets for the same ID
      if (prev.has(target.id)) {
        logger.warn(`Polling target ${target.id} already exists, skipping duplicate`);
        return prev;
      }
      
      const newTargets = new Map(prev);
      newTargets.set(target.id, { ...target, lastUpdate: 0, errorCount: 0 });
      
      logger.info(`Added polling target: ${target.id} (${target.type}, ${target.priority})`);
      return newTargets;
    });
  }, []);

  // Remove a polling target
  const removeTarget = useCallback((id: string) => {
    setTargets(prev => {
      const newTargets = new Map(prev);
      newTargets.delete(id);
      return newTargets;
    });
  }, []);

  // Update target priority
  const updateTargetPriority = useCallback((id: string, priority: 'high' | 'normal' | 'low') => {
    setTargets(prev => {
      const newTargets = new Map(prev);
      const target = newTargets.get(id);
      if (target) {
        newTargets.set(id, { ...target, priority });
      }
      return newTargets;
    });
  }, []);

  // Start/stop polling based on targets
  useEffect(() => {
    if (targets.size > 0 && !isPolling) {
      setIsPolling(true);
      // Start immediately, then schedule with adaptive interval
      performBatchedPolling().then(() => {
        startPolling();
      });
    } else if (targets.size === 0 && isPolling) {
      setIsPolling(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [targets.size, isPolling, performBatchedPolling, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    addTarget,
    removeTarget,
    updateTargetPriority,
    isPolling,
    totalTargets: targets.size
  };
}; 